import React from 'react';
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Tooltip,
  Card,
  CardContent,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { config } from '../../config';
import { SnackbarContext } from '../../ui/SnackbarProvider';
import { ModeContext } from '../../theme/ThemeProvider';
import { fetchTenantSettings, saveTenantSettings, getTenantIdFromStorage } from '../../services/tenantSettingsService';
import SettingsPageHeader from './components/SettingsPageHeader';
import StickySaveBar from './components/StickySaveBar';
import BrandPreviewCard from './components/BrandPreviewCard';

const DEFAULT_PRIMARY = '#5e4db2';
const DEFAULT_SUCCESS = '#26532B';
const DEFAULT_WARNING = '#F2A900';
const DEFAULT_ERROR = '#EF233C';
const DEFAULT_CHARTS = [
  '#4E79A7', '#F28E2B', '#59A14F', '#E15759', '#B07AA1',
  '#EDC948', '#76B7B2', '#FF9DA7', '#9C755F', '#BAB0AC',
];

const cardContentSx = {
  px: { xs: 2, sm: 3 },
  py: { xs: 2, sm: 3 },
  '&:last-child': { pb: { xs: 2, sm: 3 } },
};

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
}

function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', minWidth: 0 }}>
        <Box
          sx={(t) => ({
            position: 'relative',
            flexShrink: 0,
            width: 44,
            height: 40,
            borderRadius: `${t.ds.radius.sm}px`,
            border: `1px solid ${t.tokens.border.default}`,
            overflow: 'hidden',
            bgcolor: value,
          })}
        >
          <input
            type="color"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              opacity: 0,
              cursor: 'pointer',
              border: 'none',
              padding: 0,
            }}
          />
        </Box>
        <TextField
          value={value}
          onChange={(e) => onChange(e.target.value)}
          size="small"
          fullWidth
          sx={{ minWidth: 0 }}
        />
      </Box>
    </Box>
  );
}

export default function BrandingSettingsPage() {
  const { notify } = React.useContext(SnackbarContext);
  const { refreshBranding } = React.useContext(ModeContext);
  const tenantId = React.useMemo(() => getTenantIdFromStorage(), []);

  const [primaryMain, setPrimaryMain] = React.useState<string>(DEFAULT_PRIMARY);
  const [successColor, setSuccessColor] = React.useState<string>(DEFAULT_SUCCESS);
  const [warningColor, setWarningColor] = React.useState<string>(DEFAULT_WARNING);
  const [errorColor, setErrorColor] = React.useState<string>(DEFAULT_ERROR);
  const [chartColors, setChartColors] = React.useState<string[]>(DEFAULT_CHARTS);

  const [brandName, setBrandName] = React.useState<string>(config.brandName);
  const [logoUrl, setLogoUrl] = React.useState<string>('');
  const [logoType, setLogoType] = React.useState<'icon' | 'wordmark'>('icon');
  const [useDefaultLogo, setUseDefaultLogo] = React.useState<boolean>(false);
  const [uploadingLogo, setUploadingLogo] = React.useState<boolean>(false);

  const [saving, setSaving] = React.useState<boolean>(false);

  const hydrate = React.useCallback(async () => {
    if (!tenantId) return;
    try {
      const s = await fetchTenantSettings(tenantId);
      const branding = (s['BRANDING'] || {}) as Record<string, any>;
      if (typeof branding.name === 'string') setBrandName(branding.name);
      if (typeof branding.logoUrl === 'string') {
        if (branding.logoUrl === config.logoUrl) {
          setUseDefaultLogo(true);
          setLogoUrl('');
        } else {
          setLogoUrl(branding.logoUrl);
          setUseDefaultLogo(false);
        }
      }
      if (branding.logoType === 'icon' || branding.logoType === 'wordmark') {
        setLogoType(branding.logoType);
      }
      const colors = branding.colors || {};
      if (colors.primary?.['400']) setPrimaryMain(colors.primary['400']);
      if (colors.semantic) {
        if (colors.semantic.success) setSuccessColor(colors.semantic.success);
        if (colors.semantic.warning) setWarningColor(colors.semantic.warning);
        if (colors.semantic.error) setErrorColor(colors.semantic.error);
      }
      if (Array.isArray(colors.charts) && colors.charts.length > 0) {
        // Pad/truncate to exactly 10 entries so the picker grid renders cleanly.
        const padded = [...colors.charts];
        while (padded.length < 10) padded.push(DEFAULT_CHARTS[padded.length]);
        setChartColors(padded.slice(0, 10));
      }
    } catch (e: any) {
      notify(e?.message || 'Failed to load branding', 'error');
    }
  }, [tenantId, notify]);

  React.useEffect(() => { hydrate(); }, [hydrate]);

  const handleLogoUpload = (file: File) => {
    if (file.size > 2 * 1024 * 1024) {
      notify('Logo file must be less than 2MB', 'error');
      return;
    }
    if (!['image/svg+xml', 'image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      notify('Logo must be SVG, PNG, or JPG', 'error');
      return;
    }
    setUploadingLogo(true);
    const reader = new FileReader();
    reader.onload = () => {
      setLogoUrl(reader.result as string);
      setUseDefaultLogo(false);
      setLogoType('icon');
      setUploadingLogo(false);
      notify('Logo uploaded — click Save to apply', 'success');
    };
    reader.onerror = () => {
      setUploadingLogo(false);
      notify('Failed to read logo file', 'error');
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!tenantId) {
      notify('Missing tenant', 'error');
      return;
    }
    setSaving(true);
    try {
      // Preserve sibling fields on BRANDING that are owned by other settings
      // pages (sidebarPosition, defaultHomePage live on Workspace).
      await saveTenantSettings(tenantId, (current) => {
        const existing = (current.BRANDING as Record<string, any>) || {};
        return {
          BRANDING: {
            ...existing,
            name: brandName,
            logoUrl: useDefaultLogo ? config.logoUrl : logoUrl,
            logoType: (logoUrl || useDefaultLogo) ? logoType : undefined,
            colors: {
              ...(existing.colors || {}),
              primary: { 400: primaryMain },
              semantic: {
                success: successColor,
                warning: warningColor,
                error: errorColor,
              },
              charts: chartColors,
            },
          },
        };
      });
      notify('Branding saved — applying changes', 'success');
      // Hot-reload the theme without a full page reload so any in-progress
      // chat / widget state survives.
      refreshBranding();
    } catch (e: any) {
      notify(e?.message || 'Failed to save branding', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, pb: { xs: 2, sm: 0 } }}>
      <SettingsPageHeader description="Personalize colors, logo, and brand identity for your organization." />

      {/* Brand identity */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={cardContentSx}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1rem' }}>
            Brand identity
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
            Your name and logo appear in the sidebar, login screen, and PDF exports.
          </Typography>

          <Stack spacing={2.5}>
            <TextField
              fullWidth
              label="Brand name"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              placeholder={config.brandName}
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1, fontSize: '0.875rem' }}>
                Logo
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5, fontSize: '0.75rem' }}>
                SVG, PNG, or JPG · max 2MB · square with transparent background recommended.
              </Typography>

              <ToggleButtonGroup
                value={useDefaultLogo ? 'default' : 'custom'}
                exclusive
                onChange={(_e, val: 'default' | 'custom' | null) => {
                  if (!val) return;
                  if (val === 'default') {
                    setUseDefaultLogo(true);
                    setLogoUrl('');
                  } else {
                    setUseDefaultLogo(false);
                  }
                }}
                size="small"
                sx={{ mb: 1.5, width: { xs: '100%', sm: 'auto' } }}
              >
                <ToggleButton value="default" sx={{ px: 2.5, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Default logo
                </ToggleButton>
                <ToggleButton value="custom" sx={{ px: 2.5, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                  Upload custom
                </ToggleButton>
              </ToggleButtonGroup>

              {!useDefaultLogo ? (
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={2}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploadingLogo}
                    sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, textTransform: 'none', fontWeight: 600 }}
                  >
                    {uploadingLogo ? 'Uploading…' : logoUrl ? 'Replace logo' : 'Upload logo'}
                    <input
                      type="file"
                      hidden
                      accept="image/svg+xml,image/png,image/jpeg,image/jpg"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleLogoUpload(file);
                      }}
                    />
                  </Button>
                  {logoUrl ? (
                    <Stack
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                      <Box
                        component="img"
                        src={logoUrl}
                        alt="Logo preview"
                        sx={(t) => ({
                          width: 48,
                          height: 48,
                          objectFit: 'contain',
                          border: `1px solid ${t.tokens.border.subtle}`,
                          borderRadius: `${t.ds.radius.sm}px`,
                          p: 0.5,
                          flexShrink: 0,
                          bgcolor: t.tokens.surface.raised,
                        })}
                      />
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => {
                          setLogoUrl('');
                          notify('Logo cleared — click Save to apply', 'info');
                        }}
                        sx={{ textTransform: 'none', fontWeight: 600 }}
                      >
                        Remove
                      </Button>
                    </Stack>
                  ) : null}
                </Stack>
              ) : (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    component="img"
                    src={config.logoUrl}
                    alt="Default logo"
                    sx={(t) => ({
                      width: 48,
                      height: 48,
                      objectFit: 'contain',
                      border: `1px solid ${t.tokens.border.subtle}`,
                      borderRadius: `${t.ds.radius.sm}px`,
                      p: 0.5,
                      bgcolor: t.tokens.surface.raised,
                    })}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
                    Using the {config.brandName} default logo.
                  </Typography>
                </Stack>
              )}

              {(logoUrl || useDefaultLogo) ? (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontSize: '0.75rem' }}>
                    Logo type
                  </Typography>
                  <ToggleButtonGroup
                    value={logoType}
                    exclusive
                    onChange={(_e, val: 'icon' | 'wordmark' | null) => { if (val) setLogoType(val); }}
                    size="small"
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                  >
                    <ToggleButton value="icon" sx={{ px: 2, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                      Icon (show name beside)
                    </ToggleButton>
                    <ToggleButton value="wordmark" sx={{ px: 2, textTransform: 'none', flex: { xs: 1, sm: 'none' } }}>
                      Wordmark (logo includes name)
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              ) : null}
            </Box>
          </Stack>
        </CardContent>
      </Card>

      {/* Primary color */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={cardContentSx}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1rem' }}>
            Primary color
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
            Used for buttons, links, and the active state on navigation.
          </Typography>
          <Box sx={{ maxWidth: 320 }}>
            <ColorField label="Primary" value={primaryMain} onChange={setPrimaryMain} />
          </Box>
        </CardContent>
      </Card>

      {/* Semantic colors */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={cardContentSx}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1rem' }}>
            Semantic colors
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
            Used for success, warning, and error states throughout the app.
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
            <ColorField label="Success" value={successColor} onChange={setSuccessColor} />
            <ColorField label="Warning" value={warningColor} onChange={setWarningColor} />
            <ColorField label="Error" value={errorColor} onChange={setErrorColor} />
          </Stack>
        </CardContent>
      </Card>

      {/* Chart palette */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={cardContentSx}>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 0.5, fontSize: '1rem' }}>
            Chart palette
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, fontSize: '0.8125rem' }}>
            10 colors used for series in charts and data visualizations. The palette wraps when more than 10 series are needed.
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: 'repeat(2, minmax(0, 1fr))',
                sm: 'repeat(3, minmax(0, 1fr))',
                md: 'repeat(5, minmax(0, 1fr))',
              },
              gap: { xs: 2, sm: 2.5 },
              maxWidth: 1100,
            }}
          >
            {chartColors.map((color, index) => (
              <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 0 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                >
                  Series {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                  <Tooltip title={color} placement="top">
                    <Box
                      sx={(t) => ({
                        position: 'relative',
                        flexShrink: 0,
                        width: 44,
                        height: 40,
                        borderRadius: `${t.ds.radius.sm}px`,
                        border: `1px solid ${t.tokens.border.default}`,
                        overflow: 'hidden',
                        bgcolor: color,
                      })}
                    >
                      <input
                        type="color"
                        value={color}
                        onChange={(e) => {
                          const next = [...chartColors];
                          next[index] = e.target.value;
                          setChartColors(next);
                        }}
                        style={{
                          position: 'absolute',
                          inset: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer',
                          border: 'none',
                          padding: 0,
                        }}
                      />
                    </Box>
                  </Tooltip>
                  <TextField
                    value={color}
                    onChange={(e) => {
                      const next = [...chartColors];
                      next[index] = e.target.value;
                      setChartColors(next);
                    }}
                    size="small"
                    placeholder="#4E79A7"
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* Live preview */}
      <BrandPreviewCard
        primary={primaryMain}
        successColor={successColor}
        warningColor={warningColor}
        errorColor={errorColor}
        chartColors={chartColors}
      />

      <StickySaveBar
        description="Branding applies tenant-wide. Changes hot-reload — no page refresh required."
        saveLabel="Save Branding"
        onSave={save}
        onReset={hydrate}
        saving={saving}
      />
    </Box>
  );
}
